import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, catchError, combineLatest, filter, finalize, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

interface AuthChannelLogoutMessage {
  type: 'logout';
  reason?: string;
}

export interface roleAccess {
  institutions: any,
  rolePermissions: {
    reports: {
      view: boolean
    },
    knowledgeBase: {
      view: boolean,
      manage: boolean,
    }
  },
  userType: string,
  department: any,
  superAdmin: boolean,
  email: string,
  status: string,
  sub: string,
  iss: string,
  iat: number,
  exp: number
}

@Injectable({
  providedIn: 'root',
})
export class StateManagementService {
  private isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private userNameSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private isValidUserSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isSuperAdminSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isSessionTimedOutSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private sessionTimeoutReasonSubject: BehaviorSubject<'inactive' | 'expired'> = new BehaviorSubject<'inactive' | 'expired'>('inactive');
  private refreshTokenTimer: ReturnType<typeof setInterval> | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly refershtoken = 60 * 1000;
  private readonly refreshThresholdSeconds = 5 * 60;
  private readonly inactivityTimeoutMs = 15 * 60 * 1000;
  private readonly sessionTimeoutWarningMs = 5 * 60 * 1000;
  private readonly inactivityDeadlineStorageKey = 'inactivityDeadlineAt';
  private readonly warningDeadlineStorageKey = 'warningDeadlineAt';
  private tokenExpiryHandled = false;
  private revalidation$: Observable<boolean> | null = null;
  private lastActiveRoute = '/dashboard';
  private auth = inject(AuthService);
  private channel = new BroadcastChannel('auth');
  private router = inject(Router);
  private readonly runtimeApiUrl = (window as { __APP_CONFIG__?: { apiUrl?: string } })
    .__APP_CONFIG__?.apiUrl;
  private readonly baseApiUrl = this.runtimeApiUrl || environment.apiUrl;
  private roleAccess: roleAccess | null = null;
  // {
  //   institutions: [],
  //   rolePermissions: {
  //     reports: {
  //       view: true
  //     },
  //     knowledgeBase: {
  //       view: true,
  //       manage: true,
  //     }
  //   },
  //   userType: '',
  //   department: 'any',
  //   superAdmin: true,
  //   email: '',
  //   status: '',
  //   sub: '',
  //   iss: '',
  //   iat: 0,
  //   exp: 0
  // };
  public roleAccessSubject: BehaviorSubject<roleAccess | null> = new BehaviorSubject<roleAccess | null>(null);


  private setLocalStorage(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getLocalStorage(key: string): string | null {
    return localStorage.getItem(key);
  }

  removeLocalStorage(key: string): void {
    localStorage.removeItem(key);
  }

  private setSessionStorage(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  private removeSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
  }

  constructor(private http: HttpClient) {
    this.loadSessionAuth();
    this.watchAuthState();
    this.registerActivityListeners();
    window.addEventListener('focus', this.handleAppResume);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.channel.onmessage = (event) => {
      console.log('Received message on auth channel:', event.data);
      const isStringLogout = event.data === 'logout';
      const isObjectLogout =
        !!event.data &&
        typeof event.data === 'object' &&
        (event.data as { type?: string }).type === 'logout';

      if (isStringLogout || isObjectLogout) {
        const logoutReason = isObjectLogout
          ? (event.data as AuthChannelLogoutMessage).reason
          : undefined;

        sessionStorage.clear();
        this.removeLocalStorage('isValidUser');
        this.removeLocalStorage('isAuthenticated');
        this.removeLocalStorage('jwtToken');
        this.removeLocalStorage('userValue');
        this.removeLocalStorage('platformToken');
        this.removeLocalStorage('userRoleAccessToken');
        this.exitSessionTimeoutState();
        this.setLoggedInStatus(false);
        this.setIsValidUser(false);
        this.clearRefreshTimer();

        if (logoutReason) {
          this.router.navigate(['/login'], { queryParams: { reason: logoutReason } });
        } else {
          this.router.navigate(['/login']);
        }
      }
      if (event.data === 'login') {
        this.exitSessionTimeoutState();
        this.loadSessionAuth();
        // this.router.navigate(['/dashboard']);
        if (!localStorage.getItem('userRoleAccessToken')) {
          this.router.navigate(['dashboard']);
        } else {
          const decoded = JSON.parse(localStorage.getItem('userRoleAccessToken') || '') as roleAccess | null;
          if (decoded?.rolePermissions?.reports.view == true) {
            this.router.navigate(['dashboard']);
          } else if (decoded?.rolePermissions?.knowledgeBase.view == true || decoded?.rolePermissions?.knowledgeBase.manage == true) {
            this.router.navigate(['kb']);
          }
        }
      }
    };
  }

  private registerActivityListeners(): void {
    ['click', 'keydown', 'scroll', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, this.handleUserActivity);
    });
  }

  private handleUserActivity = (): void => {
    if (
      !this.isAuthenticatedSubject.getValue() ||
      !this.isValidUserSubject.getValue() ||
      this.isSessionTimedOutSubject.getValue()
    ) {
      return;
    }
    this.startInactivityTimer();
  };

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private startInactivityTimer(): void {
    this.clearInactivityTimer();
    const deadlineAt = Date.now() + this.inactivityTimeoutMs;
    const warningDeadline = deadlineAt + this.sessionTimeoutWarningMs;

    // Save the deadline
    this.setLocalStorage(this.inactivityDeadlineStorageKey, String(deadlineAt));
    this.setLocalStorage(this.warningDeadlineStorageKey, String(warningDeadline));
    this.inactivityTimer = setTimeout(() => {
      this.triggerSessionTimeout('inactive');
    }, this.inactivityTimeoutMs);
  }

  private getStoredWarningDeadline(): number | null {
    const value = this.getLocalStorage(this.warningDeadlineStorageKey);
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private isPublicRoute(route: string): boolean {
    return ['/login', '/user/invalid', '/user/validate', '/session-timeout', '/'].includes(route);
  }

  private triggerSessionTimeout(reason: 'inactive' | 'expired'): void {
    if (this.isSessionTimedOutSubject.getValue()) {
      return;
    }

    this.clearRefreshTimer();
    this.clearInactivityTimer();

    const currentRoute = this.router.url.split('?')[0] || '/dashboard';
    if (!this.isPublicRoute(currentRoute)) {
      this.lastActiveRoute = currentRoute;
    }

    this.isSessionTimedOutSubject.next(true);
    this.sessionTimeoutReasonSubject.next(reason);
  }

  private exitSessionTimeoutState(): void {
    this.isSessionTimedOutSubject.next(false);
    this.sessionTimeoutReasonSubject.next('inactive');
  }

  public decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return null;
      }
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
        '=',
      );
      return JSON.parse(atob(paddedPayload));
    } catch {
      return null;
    }
  }

  private isSuperAdminFromToken(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    return payload?.['superAdmin'] === true;
  }

  private handleAppResume = (): void => {
    // When the system wakes from sleep, refresh timer callbacks may have been paused.
    // Trigger a refresh check immediately to avoid first API call failures.
    if (!this.isAuthenticatedSubject.getValue() || !this.isValidUserSubject.getValue()) {
      return;
    }
    const deadline = this.getStoredInactivityDeadline();
    const warningDeadline = this.getStoredWarningDeadline();
    // Entire warning period already passed
    if (warningDeadline && Date.now() >= warningDeadline) {
      this.clearAuthStateForLogout();
      this.router.navigate(['/login'], { queryParams: { reason: 'session-timeout' } });
      return;
    }
    if (deadline && Date.now() >= deadline) {
      // Only show the timeout popup
      this.triggerSessionTimeout('inactive');
      return;
    }
    this.refreshUserToken().subscribe();
  };
  private getStoredInactivityDeadline(): number | null {
    const value = this.getLocalStorage(this.inactivityDeadlineStorageKey);
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.handleAppResume();
    }
  };

  private clearAuthStateStorage(resetSessionTimeout: boolean = true): void {
    this.clearRefreshTimer();
    this.clearInactivityTimer();
    this.removeLocalStorage(this.inactivityDeadlineStorageKey);
    this.isAuthenticatedSubject.next(false);
    this.isValidUserSubject.next(false);
    this.userNameSubject.next('');
    this.isSuperAdminSubject.next(false);
    this.roleAccess = null;
    this.roleAccessSubject.next(null);

    if (resetSessionTimeout) {
      this.exitSessionTimeoutState();
    }

    this.removeSessionStorage('isAuthenticated');
    this.removeSessionStorage('isValidUser');

    this.removeLocalStorage('isAuthenticated');
    this.removeLocalStorage('isValidUser');
    this.removeLocalStorage('jwtToken');
    this.removeLocalStorage('userValue');
    this.removeLocalStorage('roleAccessToken');
    this.removeLocalStorage('kb.selectedDepartmentPk');
    this.removeLocalStorage('kb.selectedInstitutionPk');
    this.removeLocalStorage('selectedArticle');
    this.removeLocalStorage('departments');

  }

  clearAuthStateForLogout(): void {
    this.clearAuthStateStorage();
  }

  private loadSessionAuth(): void {
    this.setLoggedInStatus(this.getLocalStorage('isAuthenticated') === 'true');
    this.setIsValidUser(this.getLocalStorage('isValidUser') === 'true');

    const jwtToken = this.getLocalStorage('jwtToken');
    if (jwtToken) {
      this.isSuperAdminSubject.next(this.isSuperAdminFromToken(jwtToken));
      this.startTokenRefreshPolling();
    }

    // Load role access token if exists
    // The role access JWT is stored under 'platformToken' (see validateUser/refreshUserToken)
    const roleAccessToken = this.getLocalStorage('platformToken') || this.getLocalStorage('roleAccessToken');
    if (roleAccessToken) {
      this.setRoleAccess(roleAccessToken);
    }

    const userValue = this.getLocalStorage('userValue');
    if (!userValue) {
      return;
    }

    try {
      const user = JSON.parse(userValue) as {
        email?: string;
        firstName?: string;
        lastName?: string;
        superAdmin?: boolean;
      };
      const displayName = user.email
      if (displayName) {
        this.setUserName(displayName);
      }
    } catch {
      this.removeLocalStorage('userValue');
    }
  }

  private watchAuthState(): void {
    combineLatest([this.auth.isAuthenticated$, this.auth.isLoading$])
      .pipe(filter(([, isLoading]) => !isLoading))
      .subscribe(([isAuthenticated]) => {
        if (!isAuthenticated) {
          this.clearAuthStateStorage();
          return;
        }
        this.setLoggedInStatus(true);
      });

    this.auth.user$.subscribe((user) => {
      const name = user?.name
      if (name) {
        this.setUserName(name);
      }
    });
  }

  setLoggedInStatus(isLoggedIn: boolean): void {
    this.isAuthenticatedSubject.next(isLoggedIn);
    this.setSessionStorage('isAuthenticated', String(isLoggedIn));
    this.setLocalStorage('isAuthenticated', String(isLoggedIn));

    if (!isLoggedIn) {
      this.clearInactivityTimer();
      return;
    }

    if (this.isValidUserSubject.getValue() && !this.isSessionTimedOutSubject.getValue()) {
      this.startInactivityTimer();
    }
  }


  getAuthenticationStatus(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  setUserName(userName: string): void {
    this.userNameSubject.next(userName);
  }

  getUserName(): Observable<string> {
    return this.userNameSubject.asObservable();
  }

  setIsValidUser(isValid: boolean): void {
    this.isValidUserSubject.next(isValid);
    this.setSessionStorage('isValidUser', String(isValid));
    this.setLocalStorage('isValidUser', String(isValid));

    if (!isValid) {
      this.clearInactivityTimer();
      return;
    }

    if (this.isAuthenticatedSubject.getValue() && !this.isSessionTimedOutSubject.getValue()) {
      this.startInactivityTimer();
    }
  }

  getIsValidUser(): Observable<boolean> {
    return this.isValidUserSubject.asObservable();
  }

  getJwtToken(): string | null {
    return this.getLocalStorage('jwtToken');
  }

  getPlatformToken(): string | null {
    return this.getLocalStorage('platformToken');
  }
  getIsSuperAdmin(): Observable<boolean> {
    return this.isSuperAdminSubject.asObservable();
  }

  isSuperAdmin(): boolean {
    return this.isSuperAdminSubject.getValue();
  }

  setRoleAccess(token: string): void {
    const decoded = this.decodeJwtPayload(token) as roleAccess | null;
    if (decoded) {
      console.log('Decoded role access token:', JSON.stringify(decoded));
      this.roleAccess = decoded;
      this.roleAccessSubject.next(decoded);
      this.setLocalStorage('userRoleAccessToken', JSON.stringify(decoded));
    }
  }

  getRoleAccess(): roleAccess | null {
    return this.roleAccess;
  }

  getRoleAccessObservable(): Observable<roleAccess | null> {
    return this.roleAccessSubject.asObservable();
  }

  hasKnowledgeBasePermission(permission: 'view' | 'manage'): boolean {
    if (!this.roleAccess?.rolePermissions?.knowledgeBase) {
      return false;
    }
    return this.roleAccess.rolePermissions.knowledgeBase[permission] === true;
  }

  hasReportsViewPermission(): boolean {
    return this.roleAccess?.rolePermissions?.reports?.view === true;
  }

  getUserInstitutions(): string[] {
    return this.roleAccess?.institutions || [];
  }

  getUserDepartments(): string[] {
    return this.roleAccess?.department || [];
  }

  getUserType(): string | null {
    return this.roleAccess?.userType || null;
  }

  private clearRefreshTimer(): void {
    if (this.refreshTokenTimer) {
      clearInterval(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }
  }

  handleTokenExpired(): void {
    if (this.tokenExpiryHandled) {
      return;
    }
    if (!this.isAuthenticatedSubject.getValue() || !this.isValidUserSubject.getValue()) {
      this.clearAuthStateStorage();
      return;
    }
    this.tokenExpiryHandled = true;
    this.clearAuthStateStorage(false);
  }

  getSessionTimeoutReason(): Observable<'inactive' | 'expired'> {
    return this.sessionTimeoutReasonSubject.asObservable();
  }

  getIsSessionTimedOut(): Observable<boolean> {
    return this.isSessionTimedOutSubject.asObservable();
  }

  getLastActiveRoute(): string {
    return this.lastActiveRoute;
  }

  continueSession(): Observable<boolean> {
    if (!this.isSessionTimedOutSubject.getValue()) {
      return of(true);
    }

    const token = this.getLocalStorage('jwtToken');
    if (!token) {
      return of(false);
    }

    return this.refreshUserToken().pipe(
      tap((isSessionActive) => {
        if (!isSessionActive) {
          return;
        }

        this.tokenExpiryHandled = false;
        this.exitSessionTimeoutState();
        this.setLoggedInStatus(true);
        this.setIsValidUser(true);
        this.startTokenRefreshPolling();
        this.startInactivityTimer();
        this.router.navigateByUrl(this.lastActiveRoute || '/dashboard');
      }),
    );
  }

  private startTokenRefreshPolling(): void {
    this.clearRefreshTimer();

    if (!this.getLocalStorage('jwtToken')) {
      return;
    }

    // Trigger one refresh attempt immediately so the session stays warm
    // even before the first interval tick.
    this.refreshUserToken().subscribe();

    this.refreshTokenTimer = setInterval(() => {
      this.refreshUserToken().subscribe();
    }, this.refershtoken);
  }

  private shouldRefreshToken(token: string): boolean {
    const payload = this.decodeJwtPayload(token) as { exp?: number } | null;

    if (!payload?.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = payload.exp - currentTime;

    return remainingSeconds <= this.refreshThresholdSeconds;
  }

  // Call refresh API only in the last 5 minutes before expiry.
  refreshUserToken(): Observable<boolean> {
    const token = this.getLocalStorage('jwtToken');

    if (!token) {
      return of(false);
    }
    if (!this.shouldRefreshToken(token)) {
      return of(true);
    }
    return this.http
      .post<any>(
        `${this.baseApiUrl}/users/refresh`,
        {},
        {
          observe: 'response',
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
          }),
        },
      )
      .pipe(
        map((response) => {
          const newToken = response.body?.jwtToken;
          const platformToken = response.body?.platformAPIToken;
          if (!newToken) {
            this.handleTokenExpired();
            return false;
          }

          this.setLocalStorage('jwtToken', newToken);

          this.setLocalStorage('platformToken', platformToken);
          this.setRoleAccess(platformToken);
          this.isSuperAdminSubject.next(this.isSuperAdminFromToken(newToken));
          return true;
        }),
        // 401/403 session handling lives in authInterceptor.
        catchError(() => of(false)),
      );
  }

  // Re-validates the signed-in user after a 401/403. Concurrent callers share one validate call.
  revalidateSession(): Observable<boolean> {
    // Logged-out or timed-out sessions must not be silently restored by a late API failure.
    if (
      !this.isAuthenticatedSubject.getValue() ||
      !this.isValidUserSubject.getValue() ||
      this.isSessionTimedOutSubject.getValue()
    ) {
      return of(false);
    }

    this.revalidation$ ??= this.auth.idTokenClaims$.pipe(
      take(1),
      switchMap((claims) => claims?.__raw ? this.validateUser(claims.__raw) : of(false)),
      catchError(() => of(false)),
      finalize(() => {
        this.revalidation$ = null;
      }),
      shareReplay(1),
    );

    return this.revalidation$;
  }

  validateUser(idToken: string): Observable<boolean> {
    const existingJwt = this.getLocalStorage('jwtToken');
    const requestHeaders = existingJwt
      ? new HttpHeaders({ Authorization: `Bearer ${existingJwt}` })
      : undefined;

    return this.http
      .post<any>(`${this.baseApiUrl}/users/validate`, { idToken }, {
        observe: 'response',
        headers: requestHeaders,
      })
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            this.setLocalStorage('userValue', JSON.stringify(response.body));
            this.setSessionStorage('isValidUser', 'true');
            this.setLocalStorage('isValidUser', 'true');
            const token = (response.body as any)?.jwtToken;
            const platformToken = (response.body as any)?.platformToken;
            const platformAPIToken = (response.body as any)?.platformAPIToken;
            if (token) {
              this.tokenExpiryHandled = false;
              this.setLocalStorage('jwtToken', token);
              this.isSuperAdminSubject.next(this.isSuperAdminFromToken(token));
              this.startTokenRefreshPolling();
              if (platformToken) {
                this.setLocalStorage('platformToken', platformToken);
                this.setRoleAccess(platformToken);
                try {
                  this.channel.postMessage({ type: 'loadNavItems' });
                } catch (err) {
                  //this.channel.postMessage({ type: 'loadNavItems' });
                  console.warn('Failed to post loadNavItems message on auth channel', err);
                }
                console.log('Platform token stored successfully');
              }
            } else {
              this.isSuperAdminSubject.next(false);
            }
            return true;
          } else {
            this.removeLocalStorage('userValue');
            this.setSessionStorage('isValidUser', 'false');
            this.setLocalStorage('isValidUser', 'false');
            this.isSuperAdminSubject.next(false);
            // route to login page with query param reason=session-timeout
            this.router.navigate(['/login'], { queryParams: { reason: 'expired' } });
            return false;
          }
        }),
        tap((isValid: boolean) => {
          console.log('Validate User status:', isValid);
        }),
        catchError((error) => {
          this.removeLocalStorage('userValue');
          this.setSessionStorage('isValidUser', 'false');
          this.setLocalStorage('isValidUser', 'false');
          this.isSuperAdminSubject.next(false);
          return of(false);
        }),
      );
  }
}